import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Input, Select, Table, Tabs, message } from "antd";
import {
  ArrowRightOutlined,
  CaretDownOutlined,
  CaretUpOutlined,
  CloudUploadOutlined,
  DeleteOutlined,
  DownloadOutlined,
  ExclamationCircleOutlined,
  FileOutlined,
  RightOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useRecoilValue } from "recoil";
import { memberState } from "../../../../state/atoms";
import DataKitApi from "../../../../api/DataKitApi";
import {
  DataKitDto,
  DataKitImportPreviewDto,
  DataKitItemDto,
  ImportMappingDto,
  ImportParseDto,
  ImportRowDto,
} from "../../../../domain/DataKitDto";
import { env } from "../../../../env/env";
import { hasDataKitBeta } from "./dataKitAccess";
import {
  FIRST_PAIR,
  MAPPING_SECTIONS,
  REPEATABLE,
  needsPairIndex,
  optionValueOf,
  parseOptionValue,
  sectionOfTarget,
  slotLabel,
  splitSuggestedTarget,
  validateMapping,
} from "./dataKitImportMapping";
import { DataKitImportDecisions } from "./DataKitImportDecisions";
import { CardResult, DecisionState, cardsOf, emptyDecisionState, resolveCard } from "./dataKitImportChoices";
import "./DataKitImportPage.scss";

/** Numbered targets that share their slot numbers - Size 2 belongs with Price 2. */
const SLOT_PARTNER: Record<string, string> = {
  SIZE: "PRICE",
  PRICE: "SIZE",
  MODIFIER_GROUP: "MODIFIERS",
  MODIFIERS: "MODIFIER_GROUP",
};

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

const pathKeyOf = (path: string[]) => path.map((p) => p.trim().toLowerCase()).join("\u001f");

interface GroupNode {
  key: string;
  name: string;
  depth: number;
  rows: number;
  inKit: boolean;
  children: GroupNode[];
}

const pathOf = (row: ImportRowDto) =>
  row.groupPath && row.groupPath.length > 0 ? row.groupPath : [row.groupName];

/** Every group the row files its item in: its own, then any the server folded in. */
const pathsOf = (row: ImportRowDto) => [pathOf(row), ...(row.alsoInGroupPaths || [])];

const groupLabel = (row: ImportRowDto) => pathsOf(row).map((p) => p.join(" › ")).join(" · ");

const priceOf = (row: ImportRowDto) =>
  (row.prices || []).map((p) => p.price).filter(Boolean).join(" / ") || "––";

/**
 * The spreadsheet importer, as its own page under the kit.
 *
 * It was a modal whose review step paged through every row 8 at a time, with the controls moving
 * as you paged, and whose only answer to "these rows share a name" was to skip them silently. The
 * three steps are the same three Timber's importer has — upload, match, review — and the review
 * step is tabbed rather than paginated so a decision is never more than one click away and nothing
 * is hidden behind a page number.
 *
 * Import stays all-or-nothing: nothing is written until the last button, and the server recomputes
 * its own plan rather than trusting this one (a kit can change between a preview and an apply).
 */
export function DataKitImportPage() {
  const { kitId } = useParams();
  const navigate = useNavigate();
  const member = useRecoilValue(memberState);
  const canWrite = !!member && (member.owner || member.role === "Admin");

  const [kit, setKit] = useState<DataKitDto | null>(null);
  const [kitGroupKeys, setKitGroupKeys] = useState<Set<string>>(new Set());
  const [step, setStep] = useState(1);
  const [file, setFile] = useState<File | null>(null);
  const [dragging, setDragging] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [parsed, setParsed] = useState<ImportParseDto | null>(null);
  const [unsuggested, setUnsuggested] = useState<Set<number>>(new Set());
  const [mapping, setMapping] = useState<ImportMappingDto[]>([]);
  const [preview, setPreview] = useState<DataKitImportPreviewDto | null>(null);
  const [decisionState, setDecisionState] = useState<DecisionState>(emptyDecisionState());
  const [skippedNew, setSkippedNew] = useState<Record<number, boolean>>({});
  const [openSets, setOpenSets] = useState<Record<string, boolean>>({});
  /** The kit's items, so a decision row can name the item it would update rather than an id. */
  const [kitItems, setKitItems] = useState<DataKitItemDto[]>([]);
  const [tab, setTab] = useState("decide");
  const [search, setSearch] = useState("");
  const [groupSortDesc, setGroupSortDesc] = useState(false);
  const [busy, setBusy] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  const kitPath = `${env.dashboardPath}/account/data-kits/${kitId}`;
  const kitName = kit?.name || "this library";

  useEffect(() => {
    if (!kitId) return;
    DataKitApi.getKit(kitId)
      .then((res) => setKit(res.data))
      .catch(() => message.error("Could not load this item library"));
    // Only to mark which of the file's groups the kit already has; the import works without it.
    DataKitApi.listGroups(kitId)
      .then((res) =>
        setKitGroupKeys(
          new Set(res.data.map((g) => pathKeyOf(g.path && g.path.length > 0 ? g.path : [g.name])))
        )
      )
      .catch(() => undefined);
  }, [kitId]);

  const errors = useMemo(() => validateMapping(mapping), [mapping]);
  const missingRequired = useMemo(
    () => !mapping.some((m) => m.target === "GROUP") || !mapping.some((m) => m.target === "NAME"),
    [mapping]
  );
  // "Map one column to ..." is what the Required banner already says, in the design's words.
  const otherErrors = useMemo(() => errors.filter((e) => !e.startsWith("Map one column to")), [errors]);

  /**
   * Read as soon as a file is chosen, so the file card can show its row and column counts the way
   * Timber's does, and Next only moves on. Nothing is written by a parse.
   */
  const parseFile = (next: File) => {
    if (!kitId) return;
    setParsing(true);
    DataKitApi.parseImport(kitId, next)
      .then((res) => {
        const data = res.data;
        setParsed(data);
        setPreview(null);
        const blank = new Set<number>();
        setMapping(
          data.headers.map((_, columnIndex) => {
            const suggestion = data.suggestedMapping.find((s) => s.columnIndex === columnIndex);
            if (!suggestion?.target) blank.add(columnIndex);
            const { target, pairIndex } = splitSuggestedTarget(suggestion?.target || "");
            return { columnIndex, target, pairIndex };
          })
        );
        setUnsuggested(blank);
      })
      .catch(() => {
        message.error("Could not read that file");
        setFile(null);
        setParsed(null);
      })
      .finally(() => setParsing(false));
  };

  const takeFile = (next: File | null) => {
    if (!next) return;
    if (!/\.(xlsx|xls|csv)$/i.test(next.name)) {
      message.error("Choose a .xlsx, .xls or .csv file");
      return;
    }
    setFile(next);
    setParsed(null);
    parseFile(next);
  };

  const removeFile = () => {
    setFile(null);
    setParsed(null);
    setPreview(null);
    setMapping([]);
    if (fileInput.current) fileInput.current.value = "";
  };

  const runPreview = () => {
    if (!parsed || !kitId) return;
    setBusy(true);
    DataKitApi.listItems(kitId, true)
      .then((res) => setKitItems(res.data || []))
      .catch(() => undefined);
    DataKitApi.previewImport(kitId, {
      headers: parsed.headers,
      rows: parsed.rows,
      rowNumbers: parsed.rowNumbers,
      mapping: mapping.filter((m) => m.target !== "IGNORE"),
      fileName: parsed.fileName,
      resolutions: {},
    })
      .then((res) => {
        const data = res.data;
        setPreview(data);
        // Same-name cards start on "Keep as separate items"; every other card starts unanswered.
        setDecisionState(emptyDecisionState());
        setSkippedNew({});
        setOpenSets({});
        setSearch("");
        setTab((data.ambiguous || []).length > 0 ? "decide" : "new");
        setStep(3);
      })
      .catch(() => message.error("Could not preview this import"))
      .finally(() => setBusy(false));
  };

  const setTargetValue = (columnIndex: number, value: string) => {
    const { target, pairIndex } = parseOptionValue(value);
    setMapping((current) =>
      current.map((m) => (m.columnIndex === columnIndex ? { ...m, target, pairIndex } : m))
    );
  };

  /** Which column holds each once-per-file field or numbered slot, so the dropdown can say so. */
  const slotOwners = useMemo(() => {
    const owners = new Map<string, number>();
    mapping.forEach((m) => {
      if (REPEATABLE.includes(m.target)) return;
      owners.set(optionValueOf(m.target, m.pairIndex), m.columnIndex);
    });
    return owners;
  }, [mapping]);

  /** The highest slot in use per numbered target, partners counted together (Size 2 ↔ Price 2). */
  const highestSlot = useCallback(
    (target: string) =>
      mapping
        .filter((m) => m.target === target || m.target === SLOT_PARTNER[target])
        .reduce((max, m) => Math.max(max, m.pairIndex ?? FIRST_PAIR), 0),
    [mapping]
  );

  const optionsFor = (row: ImportMappingDto) => {
    const taken = (value: string) => {
      const owner = slotOwners.get(value);
      return owner !== undefined && owner !== row.columnIndex;
    };
    const option = (value: string, label: string) => ({
      value,
      disabled: taken(value),
      label: taken(value) ? `${label} — already mapped` : label,
    });
    return [
      { value: "IGNORE", label: "Don't import" },
      ...MAPPING_SECTIONS.map((section) => ({
        label: section.key === "prices" ? "Prices" : section.label,
        options: section.targets.flatMap((t) => {
          if (!needsPairIndex(t.value)) return [option(t.value, t.label)];
          // Every slot already in use, then the next one - Timber's "the next pair is offered".
          const own = row.target === t.value ? row.pairIndex ?? FIRST_PAIR : 0;
          const last = Math.max(highestSlot(t.value) + 1, own);
          return Array.from({ length: last }, (_, i) =>
            option(`${t.value}:${i + FIRST_PAIR}`, slotLabel(t.value, i + FIRST_PAIR))
          );
        }),
      })),
    ];
  };

  const mapRowsBySection = useMemo(() => {
    const order = (target: string) =>
      MAPPING_SECTIONS.flatMap((s) => s.targets.map((t) => t.value)).indexOf(target);
    const bySection: Record<string, ImportMappingDto[]> = {};
    mapping.forEach((m) => {
      const key = sectionOfTarget(m.target);
      bySection[key] = [...(bySection[key] || []), m];
    });
    Object.keys(bySection).forEach((key) => {
      bySection[key].sort((a, b) =>
        key === "prices"
          ? (a.pairIndex ?? FIRST_PAIR) - (b.pairIndex ?? FIRST_PAIR) || order(a.target) - order(b.target)
          : order(a.target) - order(b.target) || a.columnIndex - b.columnIndex
      );
    });
    return bySection;
  }, [mapping]);

  const decisionCards = useMemo(() => cardsOf(preview?.ambiguous || []), [preview]);

  const cardResults = useMemo(() => {
    const map = new Map<string, CardResult>();
    decisionCards.forEach((card) => map.set(card.key, resolveCard(card, decisionState)));
    return map;
  }, [decisionCards, decisionState]);

  /** Cards still without an answer. Import waits for all of them (Oct 9, QA). */
  const unresolvedCount = useMemo(
    () => Array.from(cardResults.values()).filter((r) => !r.resolved).length,
    [cardResults]
  );

  const keptDecisionRows = useMemo(
    () => new Set(Array.from(cardResults.values()).flatMap((r) => r.keptRows)),
    [cardResults]
  );

  const newRows = useMemo(() => preview?.creates || [], [preview]);
  const updateRows = useMemo(() => preview?.updates || [], [preview]);
  const upToDateRows = useMemo(() => preview?.alreadyUpToDate || [], [preview]);

  const filteredNewRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return newRows;
    return newRows.filter(
      (r) =>
        (r.name || "").toLowerCase().includes(q) ||
        (r.description || "").toLowerCase().includes(q) ||
        groupLabel(r).toLowerCase().includes(q)
    );
  }, [newRows, search]);

  /**
   * Every group the file files something into, nested as it will be in the kit. A parent's count
   * includes its subgroups', and groups the kit already has are marked.
   */
  const groupTree = useMemo(() => {
    const roots: GroupNode[] = [];
    const nodes = new Map<string, GroupNode>();
    const add = (row: ImportRowDto) => pathsOf(row).forEach((p) => addPath(p));
    const addPath = (rowPath: string[]) => {
      const path = rowPath.filter((p) => (p || "").trim());
      path.forEach((name, depth) => {
        const key = pathKeyOf(path.slice(0, depth + 1));
        let node = nodes.get(key);
        if (!node) {
          node = { key, name, depth, rows: 0, inKit: kitGroupKeys.has(key), children: [] };
          nodes.set(key, node);
          (depth === 0 ? roots : nodes.get(pathKeyOf(path.slice(0, depth)))?.children || roots).push(node);
        }
        node.rows += 1;
      });
    };
    newRows.filter((r) => !skippedNew[r.rowNumber]).forEach(add);
    [...updateRows, ...upToDateRows].forEach(add);
    (preview?.ambiguous || []).forEach((a) => {
      if (keptDecisionRows.has(a.row.rowNumber)) add(a.row);
    });
    const flat: GroupNode[] = [];
    const walk = (list: GroupNode[]) =>
      [...list]
        .sort((a, b) => (groupSortDesc ? -1 : 1) * a.name.localeCompare(b.name))
        .forEach((n) => {
          flat.push(n);
          walk(n.children);
        });
    walk(roots);
    return flat;
  }, [newRows, skippedNew, updateRows, upToDateRows, preview, keptDecisionRows, kitGroupKeys, groupSortDesc]);

  const newGroupCount = useMemo(() => groupTree.filter((g) => !g.inKit).length, [groupTree]);

  /** What the Import button will actually write, updating live as rows are skipped. */
  const importCount = useMemo(() => {
    const creates = newRows.filter((r) => !skippedNew[r.rowNumber]).length;
    const decided = Array.from(cardResults.values()).reduce((sum, r) => sum + r.importing, 0);
    return creates + updateRows.length + decided;
  }, [newRows, skippedNew, updateRows, cardResults]);

  const runImport = () => {
    if (!parsed || !kitId) return;
    const resolutions: Record<number, string> = Object.assign(
      {},
      ...Array.from(cardResults.values()).map((r) => r.resolutions)
    );
    // A skipped New row is dropped from the payload outright: there is no "skip" resolution for a
    // row that was never ambiguous, and the server plans from whatever rows it is handed.
    // A New row the server folded several file rows into (one item, several groups) takes those
    // rows with it, or they'd come back on their own and import in their groups.
    const skippedRowNumbers = new Set(
      newRows
        .filter((r) => skippedNew[r.rowNumber])
        .flatMap((r) => [r.rowNumber, ...(r.foldedRowNumbers || [])])
    );
    const rows = parsed.rows.filter(
      (_, i) => !skippedRowNumbers.has(parsed.rowNumbers[i])
    );
    const rowNumbers = parsed.rowNumbers.filter((n) => !skippedRowNumbers.has(n));

    setBusy(true);
    DataKitApi.applyImport(kitId, {
      headers: parsed.headers,
      rows,
      rowNumbers,
      mapping: mapping.filter((m) => m.target !== "IGNORE"),
      fileName: parsed.fileName,
      resolutions,
    })
      .then((res) => {
        const r = res.data;
        const skipped = (r.skippedByChoice || 0) + (r.ambiguous || 0);
        message.success(
          `Imported ${r.created} new and ${r.updated} updated` +
            (skipped > 0 ? `. ${plural(skipped, "row")} skipped.` : "")
        );
        navigate(kitPath);
      })
      .catch(() => message.error("Could not import this file"))
      .finally(() => setBusy(false));
  };

  if (!hasDataKitBeta(member) || !canWrite) return null;

  const candidateLabel = (id: string) => {
    const item = kitItems.find((i) => i.id === id);
    if (!item) return "Update the existing item";
    const where = (item.groups || [])
      .map((g) => (g.groupPath && g.groupPath.length > 0 ? g.groupPath : [g.groupName]).join(" › "))
      .filter(Boolean)[0];
    return `Update “${item.name}”${where ? ` in ${where}` : ""}${item.archived ? " (archived)" : ""}`;
  };

  const stepCopy: Record<number, { title: string; sub: string }> = {
    1: {
      title: "Import from spreadsheet",
      sub: "Upload a CSV or Excel file. We'll read your columns and help you match them to item fields.",
    },
    2: {
      title: "Match your columns",
      sub: "We matched your columns automatically. Adjust if needed. Columns set to Don't import are skipped.",
    },
    3: {
      title: "Review this import",
      sub: [
        plural(newRows.length, "new item"),
        updateRows.length > 0 ? `${updateRows.length} to update` : null,
        `${upToDateRows.length} already up to date`,
        plural(newGroupCount, "new group"),
      ]
        .filter(Boolean)
        .join(" • "),
    },
  };

  const renderMapRow = (row: ImportMappingDto) => (
    <div className="import-map-row" key={row.columnIndex}>
      <div className="import-map-source" title={(parsed?.rows[0] || [])[row.columnIndex] || ""}>
        {parsed?.headers[row.columnIndex] || "—"}
      </div>
      <ArrowRightOutlined className="import-map-arrow" />
      <Select
        className="import-map-select"
        value={optionValueOf(row.target, row.pairIndex)}
        onChange={(value) => setTargetValue(row.columnIndex, value)}
        options={optionsFor(row)}
        listHeight={320}
      />
    </div>
  );

  const priceRows = mapRowsBySection.prices || [];
  const pricePairs = new Set(priceRows.filter((m) => m.target === "PRICE").map((m) => m.pairIndex ?? FIRST_PAIR));

  const newColumns = [
    {
      title: "Item",
      key: "item",
      sorter: (a: ImportRowDto, b: ImportRowDto) => (a.name || "").localeCompare(b.name || ""),
      render: (_: unknown, row: ImportRowDto) => (
        <div className="import-item-cell">
          <div className="import-item-name">{row.name}</div>
          {!!row.description && <div className="import-item-desc">{row.description}</div>}
        </div>
      ),
    },
    {
      title: "Group",
      key: "group",
      width: 250,
      align: "center" as const,
      render: (_: unknown, row: ImportRowDto) => groupLabel(row),
    },
    {
      title: "Price",
      key: "price",
      width: 150,
      align: "center" as const,
      render: (_: unknown, row: ImportRowDto) => priceOf(row),
    },
    {
      title: "",
      key: "skip",
      width: 70,
      align: "right" as const,
      render: (_: unknown, row: ImportRowDto) => (
        <button
          type="button"
          className="import-link import-row-skip"
          onClick={() => setSkippedNew((c) => ({ ...c, [row.rowNumber]: !c[row.rowNumber] }))}
        >
          {skippedNew[row.rowNumber] ? "Add" : "Skip"}
        </button>
      ),
    },
  ];

  const existingCards = [
    ...updateRows.map((row) => ({ row, chip: "Will update" })),
    ...upToDateRows.map((row) => ({ row, chip: "No changes" })),
  ].sort((a, b) => a.row.rowNumber - b.row.rowNumber);

  return (
    <div className="data-kit-import-page">
      <nav className="import-breadcrumb">
        <Link to={`${env.dashboardPath}/account/data-kits`}>Item Libraries</Link>
        <RightOutlined className="import-breadcrumb-sep" />
        <Link to={kitPath}>{kit?.name || "Library"}</Link>
        <RightOutlined className="import-breadcrumb-sep" />
        <span className="import-breadcrumb-current">Import</span>
      </nav>

      <header className="import-header">
        <h1>Import items</h1>
        <p>Add items to {kitName} from a spreadsheet. Nothing is added until you finish.</p>
      </header>

      <div className="import-step-head">
        <div className="import-step-copy">
          <h2>{stepCopy[step].title}</h2>
          <p>{stepCopy[step].sub}</p>
        </div>
        <ol className="import-steps" aria-label={`Step ${step} of 3`}>
          {[1, 2, 3].map((n) => (
            <li key={n} className={n <= step ? "on" : ""}>
              {n > 1 && <span className="import-steps-line" />}
              <span className="import-steps-bubble">{n}</span>
            </li>
          ))}
        </ol>
      </div>

      {step === 1 && (
        <section className="import-step">
          <div className="import-template-bar">
            <span>Not sure about your format? Use our template</span>
            <a href={`${env.dashboardPath}/data-kit-import-template.csv`} download>
              <DownloadOutlined /> Download template
            </a>
          </div>

          <div
            className={`import-drop${dragging ? " dragging" : ""}`}
            onDragOver={(e) => {
              e.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragging(false);
              takeFile(e.dataTransfer.files?.[0] || null);
            }}
            onClick={() => fileInput.current?.click()}
          >
            <CloudUploadOutlined className="import-drop-icon" />
            <p className="import-drop-title">Drag and drop your file here</p>
            <p className="import-drop-title">
              or <span className="import-drop-link">select file from your computer</span>
            </p>
            <p className="import-drop-sub">.xlsx / .xls / .csv</p>
            <input
              ref={fileInput}
              type="file"
              accept=".xlsx,.xls,.csv"
              style={{ display: "none" }}
              onChange={(e) => takeFile(e.target.files?.[0] || null)}
            />
          </div>

          {!!file && (
            <div className="import-file-card">
              <FileOutlined className="import-file-icon" />
              <div className="import-file-info">
                <span className="import-file-name">{file.name}</span>
                <span className="import-file-meta">
                  {parsing
                    ? "Reading your file…"
                    : parsed
                    ? `${plural(parsed.rows.length, "row")} · ${plural(parsed.headers.length, "column")} detected`
                    : `${Math.max(1, Math.round(file.size / 1024))} KB`}
                </span>
              </div>
              <button type="button" className="import-file-remove" onClick={removeFile} aria-label="Remove file">
                <DeleteOutlined />
              </button>
            </div>
          )}
        </section>
      )}

      {step === 2 && !!parsed && (
        <section className="import-step">
          {(missingRequired || otherErrors.length > 0) && (
            <div className="import-error-bar">
              <ExclamationCircleOutlined className="import-error-icon" />
              <div>
                {missingRequired && (
                  <div>
                    <strong>Required:</strong> a column must be matched to Item Name and Group before
                    you can continue.
                  </div>
                )}
                {otherErrors.map((e) => (
                  <div key={e}>{e}</div>
                ))}
              </div>
            </div>
          )}

          <div className="import-map-section">
            <h3>Required</h3>
            {(mapRowsBySection.required || []).map(renderMapRow)}
            {(mapRowsBySection.required || []).length === 0 && (
              <p className="import-map-note">Choose Group and Item Name for two of the columns below.</p>
            )}
          </div>

          {(mapRowsBySection.optional || []).length > 0 && (
            <div className="import-map-section">
              <h3>Optional</h3>
              {(mapRowsBySection.optional || []).map(renderMapRow)}
            </div>
          )}

          {priceRows.length > 0 && (
            <div className="import-map-section">
              <h3>Prices — Size is optional per pair</h3>
              {priceRows.map((row, i) => (
                <div key={row.columnIndex}>
                  {i > 0 && (priceRows[i - 1].pairIndex ?? FIRST_PAIR) !== (row.pairIndex ?? FIRST_PAIR) && (
                    <hr className="import-map-divider" />
                  )}
                  {renderMapRow(row)}
                </div>
              ))}
              <p className="import-map-note">
                {plural(pricePairs.size, "price point")} detected. Each item can have any number of
                Size / Price pairs.
              </p>
            </div>
          )}

          {(mapRowsBySection.dietary || []).length > 0 && (
            <div className="import-map-section">
              <h3>Dietary / Allergen</h3>
              {(mapRowsBySection.dietary || []).map(renderMapRow)}
              <p className="import-map-note">
                Each column adds a tag, named after the column, to the items marked yes in it.
              </p>
            </div>
          )}

          {(mapRowsBySection.ignore || []).length > 0 && (
            <div className="import-map-section">
              <h3>Don&apos;t import</h3>
              {(mapRowsBySection.ignore || []).map((row) => (
                <div className="import-unmatched-row" key={row.columnIndex}>
                  <div className="import-unmatched-info">
                    <div className="import-unmatched-name">
                      &ldquo;{parsed.headers[row.columnIndex] || "—"}&rdquo;
                    </div>
                    <div className="import-unmatched-sub">
                      {unsuggested.has(row.columnIndex)
                        ? "We couldn't match this column to a menu field"
                        : "Skipped on import"}
                    </div>
                  </div>
                  <Select
                    className="import-unmatched-select"
                    value={optionValueOf(row.target, row.pairIndex)}
                    onChange={(value) => setTargetValue(row.columnIndex, value)}
                    options={optionsFor(row)}
                    dropdownMatchSelectWidth={260}
                    listHeight={320}
                  />
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {step === 3 && !!preview && (
        <section className="import-step">
          <Tabs
            className="import-review-tabs"
            activeKey={tab}
            onChange={setTab}
            items={[
              {
                key: "decide",
                label: `Needs a decision • ${decisionCards.length}`,
                children: (
                  <DataKitImportDecisions
                    cards={decisionCards}
                    results={cardResults}
                    state={decisionState}
                    onChange={setDecisionState}
                    open={openSets}
                    onToggle={(key, isOpen) => setOpenSets((c) => ({ ...c, [key]: !isOpen }))}
                    candidateLabel={candidateLabel}
                    groupLabel={groupLabel}
                    priceOf={priceOf}
                  />
                ),
              },
              {
                key: "new",
                label: `New • ${newRows.length}`,
                children: (
                  <>
                    <Input
                      className="import-search"
                      placeholder={`Search ${plural(newRows.length, "item")}`}
                      prefix={<SearchOutlined />}
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      allowClear
                    />
                    <Table
                      className="import-review-table"
                      dataSource={filteredNewRows}
                      rowKey="rowNumber"
                      pagination={false}
                      showSorterTooltip={false}
                      rowClassName={(row: ImportRowDto) =>
                        skippedNew[row.rowNumber] ? "import-row-skipped" : ""
                      }
                      columns={newColumns}
                      locale={{ emptyText: search ? "No items match your search" : "Nothing new in this file" }}
                    />
                  </>
                ),
              },
              {
                key: "existing",
                label: `Already in library • ${updateRows.length + upToDateRows.length}`,
                children: (
                  <div className="import-existing">
                    <p className="import-tab-note">
                      These rows match items already in {kitName} by name and group.
                    </p>
                    {existingCards.length === 0 && (
                      <p className="import-tab-note">Nothing in this file is already in the library.</p>
                    )}
                    {existingCards.map(({ row, chip }) => (
                      <div className="import-review-card" key={row.rowNumber}>
                        <div className="import-review-card-head">
                          <div className="import-review-info">
                            <div className="import-review-name">{row.name}</div>
                            <div className="import-review-meta">
                              {groupLabel(row)}
                            </div>
                          </div>
                          <span className="import-chip">{chip}</span>
                        </div>
                      </div>
                    ))}
                    {(preview.mergedDuplicates || []).length > 0 && (
                      <p className="import-tab-note">
                        {plural((preview.mergedDuplicates || []).length, "exact duplicate row")} merged.
                      </p>
                    )}
                  </div>
                ),
              },
              {
                key: "groups",
                label: `Groups • ${groupTree.length}`,
                children: (
                  <div className="import-groups">
                    <p className="import-tab-note">
                      The groups this file adds items to. Groups not in {kitName} yet are created on
                      import.
                    </p>
                    <div className="import-groups-table">
                      <div className="import-groups-head">
                        <button
                          type="button"
                          className="import-groups-sort"
                          onClick={() => setGroupSortDesc((v) => !v)}
                        >
                          Group {groupSortDesc ? <CaretDownOutlined /> : <CaretUpOutlined />}
                        </button>
                        <span className="import-groups-count">Items</span>
                        <span className="import-groups-chip" />
                      </div>
                      {groupTree.length === 0 && <p className="import-tab-note">No groups in this file</p>}
                      {groupTree.map((g) => (
                        <div className="import-groups-row" key={g.key} style={{ paddingLeft: 8 + g.depth * 16 }}>
                          <span className="import-groups-name">{g.name}</span>
                          <span className="import-groups-count">{g.rows}</span>
                          <span className="import-groups-chip">
                            {g.inKit && <span className="import-chip">already in library</span>}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ),
              },
            ]}
          />

          {preview.skipped.length > 0 && (
            <p className="import-tab-note">
              {plural(preview.skipped.length, "row")} skipped for having no name.
            </p>
          )}
        </section>
      )}

      <footer className="import-footer">
        {step === 3 && unresolvedCount > 0 && (
          <button type="button" className="import-unresolved" onClick={() => setTab("decide")}>
            {unresolvedCount} {unresolvedCount === 1 ? "card needs" : "cards need"} a decision before
            you can import
          </button>
        )}
        <div className="import-footer-buttons">
          <Button className="import-btn-cancel" onClick={() => navigate(kitPath)} disabled={busy}>
            Cancel
          </Button>
          {step > 1 && (
            <Button className="import-btn-cancel" onClick={() => setStep(step - 1)} disabled={busy}>
              Back
            </Button>
          )}
          {step === 1 && (
            <Button
              className="primary import-btn-next"
              onClick={() => setStep(2)}
              loading={parsing}
              disabled={!parsed}
            >
              Next
            </Button>
          )}
          {step === 2 && (
            <Button
              className="primary import-btn-next"
              onClick={runPreview}
              loading={busy}
              disabled={errors.length > 0}
            >
              Next
            </Button>
          )}
          {step === 3 && (
            <Button
              className="primary import-btn-next"
              onClick={runImport}
              loading={busy}
              disabled={importCount === 0 || unresolvedCount > 0}
            >
              Import {plural(importCount, "item")}
            </Button>
          )}
        </div>
      </footer>
    </div>
  );
}
